import { Box, Typography, Button } from "@mui/joy";
import { Visibility, VisibilityOff, TextFields, Image } from "@mui/icons-material";
import { IUnit, IUnitCompleted } from "../../../store/api/umbraco-api";
import RichText from "../../rich-text";
import { UnitProps } from "./../interfaces";
import Error from "./../shared/error";
import Loading from "./../shared/loading";
import ImageInputs from "./ImageInputs";
import TextInputs from "./text-inputs";
import { ImageAnswer, TextAnswer } from "./answer";
import useUnit from "../../../context/use-unit";
import { UnitContentResponse } from "../../../context/unit-context";
import { useMemo, useState, useEffect } from "react";
import { SecondaryActionButton } from "../shared/btn";

interface OpenExerciseQuestionResponse extends UnitContentResponse {
  unit: IUnit & {
    title: string;
    solution?: string | null;
    question: string;
    answerType: string;
    isRevealSolution: boolean;
  };
  completion: IUnitCompleted & { 
    answer: string;
    answerType?: string;
  };
}

type AnswerType = "text" | "image";

const OpenExerciseQuestion = ({ unitId, unitType }: UnitProps) => {
  const { unitContent, unitCompletion } =
    useUnit<OpenExerciseQuestionResponse>();
  const [isRevealed, setIsRevealed] = useState(false);
  const [selectedAnswerType, setSelectedAnswerType] = useState<AnswerType | null>(null);
  const [hasSelectedType, setHasSelectedType] = useState(false);

  console.log("unitContent", unitContent);
  // Initialize selectedAnswerType based on unit's answerType
  useEffect(() => {
    console.log("Unit answer type:", unitContent.data?.unit.answerType);
    if (unitContent.data?.unit.answerType === "image") {
      setSelectedAnswerType("image");
      setHasSelectedType(true);
    } else if (unitContent.data?.unit.answerType === "text") {
      setSelectedAnswerType("text");
      setHasSelectedType(true);
    } else if (unitContent.data?.unit.answerType === "both") {
      setSelectedAnswerType(null);
      setHasSelectedType(false);
    }
  }, [unitContent.data?.unit.answerType]);

  const canReveal = useMemo(
    () =>
      unitContent.data?.isRevealSolution &&
      unitContent.data.unit.solution &&
      unitContent.data.unit.solution.length > 0,
    [unitContent]
  );

  const imageUrls = useMemo(
    () => unitContent.data?.completion?.answer?.split(",") || [],
    [unitContent]
  );

  const isCompleted = useMemo(
    () =>
      unitContent.data?.isCompleted || unitCompletion.response !== undefined,
    [unitContent, unitCompletion]
  );

  const handleAnswerTypeSelect = (type: AnswerType) => {
    console.log("Answer type selected:", type);
    setSelectedAnswerType(type);
    setHasSelectedType(true);
  };

  if (unitContent.isLoading || !unitContent.data) {
    return <Loading />;
  }

  if (unitContent.isError) {
    return <Error />;
  }

  const { unit, completion } = unitContent.data;
  const isBothAnswerType = unit.answerType === "both";
  
  // Get the answer type from completion if available, otherwise use unit's answerType
  const completionAnswerType = completion?.answerType || unit.answerType;
  
  console.log("Current state:", { 
    isBothAnswerType, 
    hasSelectedType, 
    selectedAnswerType,
    unitAnswerType: unit.answerType,
    completionAnswerType
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {unit.title && (
        <Typography level="title-lg" sx={{ fontWeight: "bold" }}>
          {unit.title}
        </Typography>
      )}
      <RichText content={unit.question} />

      {!isCompleted && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {isBothAnswerType && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
              <Typography level="body-md">בחר את סוג התשובה:</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant={selectedAnswerType === "text" ? "solid" : "outlined"}
                  color="neutral"
                  onClick={() => handleAnswerTypeSelect("text")}
                  startDecorator={<TextFields />}
                  size="sm"
                >
                  טקסט
                </Button>
                <Button
                  variant={selectedAnswerType === "image" ? "solid" : "outlined"}
                  color="neutral"
                  onClick={() => handleAnswerTypeSelect("image")}
                  startDecorator={<Image />}
                  size="sm"
                >
                  תמונה
                </Button>
              </Box>
            </Box>
          )}

          {hasSelectedType && selectedAnswerType === "image" && (
            <ImageInputs
              unitId={unitId}
              unitType={unitType}
              solution={unit.solution}
              answerType={selectedAnswerType}
            />
          )}

          {hasSelectedType && selectedAnswerType === "text" && (
            <TextInputs
              unitId={unitId}
              unitType={unitType}
              solution={unit.solution}
              answerType={selectedAnswerType}
            />
          )}
        </Box>
      )}

      {canReveal && isRevealed && (
        <Box sx={{ mt: 2, p: 2, border: "1px solid", borderRadius: 2 }}>
          <Typography level="title-md" sx={{ fontWeight: "bold", mb: 1 }}>
            פתרון
          </Typography>
          <RichText content={unit.solution || ""} />
        </Box>
      )}

      {isCompleted && (
        completionAnswerType === "image" ? (
          <ImageAnswer imageUrls={imageUrls} solution={unit.solution} />
        ) : (
          <TextAnswer text={completion?.answer} solution={unit.solution} />
        )
      )}

      {!isCompleted && canReveal && (
        <SecondaryActionButton
          text={isRevealed ? "הסתר פתרון" : "הצג פתרון"}
          iconSide="left"
          icon={
            isRevealed ? (
              <VisibilityOff sx={{ fontSize: 16 }} />
            ) : (
              <Visibility sx={{ fontSize: 16 }} />
            )
          }
          onClick={() => setIsRevealed(!isRevealed)}
        />
      )}  
    </Box>
  );
};

export default OpenExerciseQuestion;
